import { useEffect, useState } from "react";
import API from "../../services/api";
import {
  Pagination,
  Modal,
  Input,
  Typography,
  Spin,
  Button,
  App,
  Empty,
} from "antd";
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import "./AdminFranchiseApprovals.css";

const { Title, Text } = Typography;

function AdminFranchiseApprovals() {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [activeFranchiseId, setActiveFranchiseId] = useState(null);

  const { message } = App.useApp();

  const openRejectModal = (id) => {
    setActiveFranchiseId(id);
    setIsRejectOpen(true);
  };

  const closeRejectModal = () => {
    setIsRejectOpen(false);
    setRejectReason("");
    setActiveFranchiseId(null);
  };

  const fetchPendingFranchises = async (page = 1) => {
    try {
      setLoading(true);
      const res = await API.get(`/franchises/pending/all?page=${page}&limit=15`);
      setFranchises(res.data.franchises || []);
      setTotal(res.data.total || 0);
      setCurrentPage(res.data.currentPage || 1);
    } catch {
      message.error("Failed to load pending franchises");
      setFranchises([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingFranchises(currentPage);
  }, [currentPage]);

  const approveFranchise = async (id) => {
    try {
      await API.put(`/franchises/approve/${id}`);
      message.success("Franchise approved successfully");
      fetchPendingFranchises(currentPage);
    } catch {
      message.error("Approval failed");
    }
  };

  const confirmRejectFranchise = async () => {
    try {
      await API.put(`/franchises/reject/${activeFranchiseId}`, {
        reason: rejectReason,
      });
      message.success("Franchise rejected with reason");
      closeRejectModal();
      fetchPendingFranchises(currentPage);
    } catch (err) {
      message.error(err.response?.data?.message || "Reject failed");
    }
  };

  if (loading) {
    return (
      <div className="fra-loader-wrap">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fra-page-wrap">
      {/* HEADER */}
      <div className="fra-banner">
        <div className="fra-banner__left">
          <Title level={3} className="fra-banner__title">
            Franchise Approvals
          </Title>
          <Text className="fra-banner__subtitle">
            Review and approve franchise applications
          </Text>
        </div>

        <div className="fra-banner__count-box">
          <span className="fra-banner__count-number">{franchises.length}</span>
          <span className="fra-banner__count-label">Pending Review</span>
        </div>
      </div>

      {/* CONTENT */}
      {franchises.length === 0 ? (
        <Empty description="No pending requests" className="fra-empty-panel" />
      ) : (
        <div className="fra-cards-grid">
          {franchises.map((franchise) => (
            <div
              key={franchise._id}
              className="fra-card"
              onClick={() => navigate(`/franchise/${franchise.slug}`)}
            >
              <div className="fra-card__media">
                <img
                  alt={franchise.brandName}
                  src={
                    franchise.images?.[0] ||
                    "https://placehold.co/400x250?text=No+Image"
                  }
                  className="fra-card__image"
                />

                <button
                  className="fra-card__view-button"
                  aria-label="View franchise"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/franchise/${franchise.slug}`);
                  }}
                >
                  <EyeOutlined />
                </button>

                <div className="fra-card__status-chip">
                  <span className="fra-card__status-dot" />
                  Pending
                </div>
              </div>

              <div className="fra-card__body">
                <h5
                  className="fra-card__title"
                  title={franchise.title || franchise.franchiseDetails?.brandName}
                >
                  {franchise.title || franchise.franchiseDetails?.brandName || "No Title"}
                </h5>

                <div className="fra-card__price-row">
                  <span className="fra-card__currency">₹</span>
                  <span className="fra-card__price-value">
                    {franchise.franchiseDetails?.investmentRequired?.toLocaleString() || 0}
                  </span>
                  <span className="fra-card__price-suffix">investment</span>
                </div>

                <div className="fra-card__meta-row">
                  <span>
                    {franchise.headquarters?.city}, {franchise.headquarters?.state}
                  </span>
                  <span className="fra-card__meta-bullet">•</span>
                  <span>{franchise.franchiseDetails?.category || "N/A"}</span>
                </div>

                <div className="fra-card__actions">
                  <Button
                    type="primary"
                    className="fra-btn fra-btn--approve"
                    icon={<CheckCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      approveFranchise(franchise._id);
                    }}
                  >
                    Approve
                  </Button>

                  <Button
                    danger
                    className="fra-btn fra-btn--reject"
                    icon={<CloseCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openRejectModal(franchise._id);
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      {franchises.length > 0 && (
        <div className="fra-pagination-shell">
          <Pagination
            current={currentPage}
            total={total}
            pageSize={15}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* REJECT MODAL */}
      <Modal
        title="Reject Franchise"
        open={isRejectOpen}
        okText="Confirm Rejection"
        cancelText="Cancel"
        okButtonProps={{ disabled: !rejectReason.trim(), danger: true }}
        onOk={confirmRejectFranchise}
        onCancel={closeRejectModal}
        className="fra-reject-modal"
      >
        <div className="fra-reject-modal__body">
          <Text strong className="fra-reject-modal__label">
            Rejection Reason
          </Text>

          <Input.TextArea
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="fra-reject-modal__textarea"
          />

          {!rejectReason.trim() && (
            <p className="fra-reject-modal__error">Reason is required</p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminFranchiseApprovals;